import React from "react";
import { FiMic, FiSquare } from "react-icons/fi";
import { VoiceInputState } from "apps/user/learner/hooks/use-voice-input";
import { useTranslation } from "lib/i18n/use-translation";
import styles from "./OriVoiceButton.module.scss";

// Center-weighted with slight asymmetry, mirroring mobile's 5-bar waveform.
const BAR_WEIGHTS = [0.5, 0.84, 1, 0.69, 0.53];
const MIN_SCALE = 0.3636;

interface OriVoiceButtonProps {
  state: VoiceInputState;
  onStart: () => void;
  onStop: () => void;
  /** Live mic loudness (0–1) driving the bars while capturing. */
  amplitude?: number;
  /** When true, bars track amplitude; otherwise they fall back to the CSS keyframe. */
  capturing?: boolean;
}

const OriVoiceButton: React.FC<OriVoiceButtonProps> = ({
  state,
  onStart,
  onStop,
  amplitude = 0,
  capturing = false,
}) => {
  const { t } = useTranslation(["ori"]);
  if (state === "listening") {
    const reactive = capturing;
    return (
      <div className={styles.listening}>
        <button
          type="button"
          className={styles.stopButton}
          onClick={onStop}
          aria-label={t("ori:chat.voiceButton.stop")}
          data-testid="ori-voice-stop"
        >
          <FiSquare className={styles.stopIcon} aria-hidden />
        </button>
        <div
          className={styles.equaliser}
          aria-hidden
          data-testid="ori-voice-equaliser"
          data-reactive={reactive || undefined}
        >
          {BAR_WEIGHTS.map((weight, index) => (
            <span
              key={index}
              className={styles.bar}
              style={
                reactive
                  ? {
                      transform: `scaleY(${(
                        MIN_SCALE +
                        (1 - MIN_SCALE) * Math.min(1, amplitude * weight)
                      ).toFixed(3)})`,
                    }
                  : undefined
              }
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={styles.micButton}
      onClick={onStart}
      aria-label={t("ori:chat.voiceButton.label")}
      data-testid="ori-voice-mic"
    >
      <FiMic className={styles.micIcon} aria-hidden />
    </button>
  );
};

export default OriVoiceButton;
